import { FC, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailTemplateConfigurationModal, {
  EmailTemplateSection,
} from 'dashboard409a/components/EmailTemplateConfigurationModal';
import { contactSelectKey } from 'dashboard409a/utilities';
import {
  COMPANY_NAME_PLACEHOLDER,
  CONTACT_NAME_PLACEHOLDER,
} from '../ProcessReportModal.templates';
import { useProcessReportModalContext } from '../ProcessReportModalContext';

const ProcessReportOutputPanel: FC = () => {
  const {
    contacts,
    isLoadingContacts,
    selectedContactKey,
    setSelectedContactKey,
    emailTemplates,
    emailPresets,
    selectedTemplateId,
    setSelectedTemplateId,
    emailSubject,
    introduction,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    isDrafting,
    draftEmail,
    draftSubject,
    handleSubjectChange,
    draftBody,
    handleBodyChange,
    saveDraftedEmail,
    draftDashboardUrl,
    dashboardOpinionUrl,
    handleCopyEmail,
    handleCopyDashboardLink,
  } = useProcessReportModalContext();

  const [emailTemplateConfigOpen, setEmailTemplateConfigOpen] = useState(false);

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        <InputLabel id="final-report-contact-label">Final Report Contact</InputLabel>
        <Select
          labelId="final-report-contact-label"
          id="final-report-contact"
          label="Final Report Contact"
          value={selectedContactKey}
          onChange={event => setSelectedContactKey(event.target.value)}
          disabled={isLoadingContacts || contacts.length === 0}
          displayEmpty
          MenuProps={{
            // Nested inside Operations Modal; keep the menu in-tree so clicks register.
            disablePortal: true,
            PaperProps: { sx: { maxHeight: 320 } },
          }}
        >
          {contacts.length === 0 && (
            <MenuItem value="" disabled>
              {isLoadingContacts ? 'Loading contacts…' : 'No contacts found'}
            </MenuItem>
          )}
          {contacts.map((contact, index) => {
            const key = contactSelectKey(contact, index);
            return (
              <MenuItem key={key} value={key}>
                {contact.name}
                {contact.email ? ` (${contact.email})` : ''}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <EmailTemplateSection
        idPrefix="final-report-email"
        templateSelectLabel="Email template"
        templates={emailTemplates}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        emailSubject={emailSubject}
        onConfigure={() => setEmailTemplateConfigOpen(true)}
      />

      <EmailTemplateConfigurationModal
        open={emailTemplateConfigOpen}
        onClose={() => setEmailTemplateConfigOpen(false)}
        idPrefix="final-report-email"
        title="Configure final report email template"
        presets={emailPresets}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        introduction={introduction}
        setIntroduction={setIntroduction}
        introductionPlaceholders={[
          { label: COMPANY_NAME_PLACEHOLDER, token: COMPANY_NAME_PLACEHOLDER },
          { label: CONTACT_NAME_PLACEHOLDER, token: CONTACT_NAME_PLACEHOLDER },
        ]}
        subjectHelperText="Use {{COMPANY_NAME}} and {{CONTACT_NAME}} as needed."
        introductionHelperText="The PDF report name and dashboard URL are added automatically."
        savePresetOpen={savePresetOpen}
        presetName={presetName}
        setPresetName={setPresetName}
        handleOpenSavePreset={handleOpenSavePreset}
        handleCloseSavePreset={handleCloseSavePreset}
        handleSavePreset={handleSavePreset}
        handleApplyPreset={handleApplyPreset}
        handleDeletePreset={handleDeletePreset}
        handleResetToDefault={handleResetToDefault}
      />

      <TextField
        label="Subject"
        value={draftSubject}
        onChange={event => handleSubjectChange(event.target.value)}
        onBlur={saveDraftedEmail}
        placeholder="Subject will appear here shortly after you start…"
        fullWidth
        size="small"
        disabled={isDrafting}
        id="final-report-email-subject"
      />
      <Box sx={{ position: 'relative' }}>
        <TextField
          label="Email"
          value={draftBody}
          onChange={event => handleBodyChange(event.target.value)}
          onBlur={saveDraftedEmail}
          placeholder="Email will appear here shortly after you start…"
          multiline
          minRows={12}
          fullWidth
          disabled={isDrafting}
          id="final-report-email-content"
        />
        {isDrafting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              borderRadius: 1,
              bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
              pointerEvents: 'none',
            }}
            data-testid="final-report-email-loading">
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Drafting email…
            </Typography>
          </Box>
        )}
      </Box>
      {draftDashboardUrl && (
        <Button
          component="a"
          href={draftDashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none' }}
          id="draft-dashboard-opinion-link"
          data-testid="draft-dashboard-opinion-link">
          View on dashboard
        </Button>
      )}
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={handleCopyEmail} disabled={!draftEmail} id="copy-final-report-email-button">
          Copy Email
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyDashboardLink}
          disabled={!dashboardOpinionUrl}
          id="copy-final-report-dashboard-link-button"
        >
          Copy Link
        </Button>
      </Stack>
    </Stack>
  );
};

export default ProcessReportOutputPanel;
